import { TableRow } from "@mui/material";
import moment, { Moment } from "moment";
import { FC, Fragment, memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { DoubleCell } from "../../components/double-cell";
import { nanoid } from "nanoid";

interface CalendarPointOfSaleTableRowInterface {
  holiday: {
    [key: string]: string;
  };
  turni?: any;
  isSingleCalendarManagement: boolean;
}
export const CalendarPointOfSaleTableRow: FC<CalendarPointOfSaleTableRowInterface> =
  memo(({ holiday, turni, isSingleCalendarManagement }) => {
    const { selectedDate } = useSelector((state: RootState) => state.rotaRoot);

    const getTurniData = useCallback((realDate: Moment, turno: any) => {
      const date = realDate.format("YYYY-MM-DD");
      let data: any = {};
      if (date in turno.extra)
        data = {
          extraId: turno.extra[date]?.id,
          isExtra: true,
          startTime1: turno.extra[date]?.startTime1,
          startTime2: turno.extra[date]?.startTime2,
          endTime1: turno.extra[date]?.endTime1,
          endTime2: turno.extra[date]?.endTime2,
          isClosed: turno.extra[date]?.isClosed,
        };

      const daysOfWeek = realDate.locale("en").format("dddd").toLowerCase();
      if (!data?.isExtra) {
        if (daysOfWeek in turno.default) {
          data = {
            isClosed:
              !turno.default[daysOfWeek]?.startTime1 &&
              !turno.default[daysOfWeek]?.startTime2 &&
              !turno.default[daysOfWeek]?.endTime1 &&
              !turno.default[daysOfWeek]?.endTime2,
            startTime1: turno.default[daysOfWeek]?.startTime1,
            startTime2: turno.default[daysOfWeek]?.startTime2,
            endTime1: turno.default[daysOfWeek]?.endTime1,
            endTime2: turno.default[daysOfWeek]?.endTime2,
          };
        } else {
          data = {
            isClosed: true,
          };
        }
      }
      return data;
    }, []);

    const getDefaultData = useCallback((realDate: Moment, turno: any) => {
      const daysOfWeek = realDate.locale("en").format("dddd").toLowerCase();

      if (daysOfWeek in turno.default) {
        return {
          isClosed:
            !turno.default[daysOfWeek]?.startTime1 &&
            !turno.default[daysOfWeek]?.startTime2 &&
            !turno.default[daysOfWeek]?.endTime1 &&
            !turno.default[daysOfWeek]?.endTime2,
          startTime1: turno.default[daysOfWeek]?.startTime1,
          startTime2: turno.default[daysOfWeek]?.startTime2,
          endTime1: turno.default[daysOfWeek]?.endTime1,
          endTime2: turno.default[daysOfWeek]?.endTime2,
        };
      }
      return undefined;
    }, []);

    const getCell = useCallback(
      (el: number) => {
        const day = el.toString();
        const realDate = moment(selectedDate).set({ date: el });
        if (!turni)
          return (
            <DoubleCell
              id={day}
              date={realDate.toDate()}
              calendarType="pointOfSale"
              shift={{ isInitial: true }}
              calendar={{ isInitial: true }}
              isSingleCalendarManagement={isSingleCalendarManagement}
            />
          );

        const date = realDate.format("YYYY-MM-DD");
        const [calendar, shift] = turni;

        let shiftData: any = getTurniData(realDate, shift),
          calendarData: any = getTurniData(realDate, calendar);

        // Nei giorni festivi la cella va mostrata chiusa, ma un eventuale
        // orario straordinario (isExtra) ha la precedenza sulla festività
        // e va mostrato regolarmente, indipendentemente per turni e calendari.
        const isHolidayDay = Boolean(holiday && date in holiday);
        if (isHolidayDay && !shiftData?.isExtra) shiftData = { isHoliday: true };
        if (isHolidayDay && !calendarData?.isExtra)
          calendarData = { isHoliday: true };

        return (
          <DoubleCell
            id={day}
            date={realDate.toDate()}
            shift={shiftData}
            calendar={calendarData}
            calendarType="pointOfSale"
            defaultShift={getDefaultData(realDate, shift)}
            defaultCalendar={getDefaultData(realDate, calendar)}
            isSingleCalendarManagement={isSingleCalendarManagement}
          />
        );
      },
      [
        getDefaultData,
        getTurniData,
        holiday,
        isSingleCalendarManagement,
        selectedDate,
        turni,
      ]
    );

    const GetTurni = useMemo(() => {
      const endOfMonth = moment(selectedDate).endOf("month");
      const calendarDays = Array.from(
        { length: endOfMonth.get("date") },
        (_, i) => i + 1
      );
      return (
        <>
          {calendarDays.map((el: number) => {
            return <Fragment key={nanoid()}>{getCell(el)}</Fragment>;
          })}
        </>
      );
    }, [getCell, selectedDate]);

    return (
      <TableRow key={nanoid()} sx={{ height: "70px" }}>
        {GetTurni}
      </TableRow>
    );
  });
